/**
 * ConfigurationDetailsPanel Component
 *
 * Side panel showing detailed information about a selected configuration.
 */

var React = require('react');
var {
  formatEnvironments,
  getEnvironmentLabel,
  formatRelativeTime,
} = require('./RemoteConfigurations.util');

/**
 * ConfigurationDetailsPanel component
 * @param {Object} props
 * @param {Object} props.configuration - Selected configuration data
 * @param {Function} props.onClose - Called when close button is clicked
 * @param {Function} props.onEdit - Called when edit button is clicked
 * @param {Function} props.onDelete - Called when delete button is clicked
 */
var ConfigurationDetailsPanel = function (props) {
  var configuration = props.configuration;
  var onClose = props.onClose;
  var onEdit = props.onEdit;
  var onDelete = props.onDelete;

  if (!configuration) {
    return null;
  }

  var isEnabled =
    configuration.default_value === true ||
    configuration.default_value === 'true';
  var environments = formatEnvironments(configuration.environments);

  var handleEditClick = function () {
    if (onEdit) {
      onEdit(configuration);
    }
  };

  var handleDeleteClick = function () {
    if (onDelete) {
      onDelete(configuration);
    }
  };

  var renderListSection = function (label, items) {
    if (!items || items.length === 0) {
      return null;
    }

    var sortedItems = items.slice().sort(function (a, b) {
      return String(a).localeCompare(String(b));
    });

    return React.createElement(
      'div',
      {
        className: 'remote-config-panel__section',
        key: label,
      },
      [
        React.createElement(
          'div',
          {
            key: 'label',
            className: 'remote-config-panel__label',
          },
          label,
        ),
        React.createElement(
          'div',
          {
            key: 'list',
            className: 'remote-config-panel__list',
          },
          sortedItems.map(function (item, index) {
            return React.createElement(
              'span',
              {
                key: item + '-' + index,
                className: 'remote-config-panel__list-item',
              },
              item,
            );
          }),
        ),
      ],
    );
  };

  return React.createElement(
    'div',
    {
      className: 'remote-config-panel',
    },
    [
      // Header
      React.createElement(
        'div',
        {
          key: 'header',
          className: 'remote-config-panel__header',
        },
        [
          React.createElement(
            'div',
            {
              key: 'title',
              className: 'remote-config-panel__title',
            },
            configuration.configuration_name,
          ),
          React.createElement(
            'button',
            {
              key: 'close',
              className: 'remote-config-panel__close',
              onClick: onClose,
              'aria-label': 'Close panel',
            },
            React.createElement(
              'svg',
              {
                width: 20,
                height: 20,
                viewBox: '0 0 24 24',
                fill: 'none',
                stroke: 'currentColor',
                strokeWidth: 2,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
              },
              [
                React.createElement('line', {
                  key: 'l1',
                  x1: 18,
                  y1: 6,
                  x2: 6,
                  y2: 18,
                }),
                React.createElement('line', {
                  key: 'l2',
                  x1: 6,
                  y1: 6,
                  x2: 18,
                  y2: 18,
                }),
              ],
            ),
          ),
        ],
      ),

      // Content
      React.createElement(
        'div',
        {
          key: 'content',
          className: 'remote-config-panel__content',
        },
        [
          // Description
          configuration.description &&
            React.createElement(
              'div',
              {
                key: 'description',
                className: 'remote-config-panel__section',
              },
              [
                React.createElement(
                  'div',
                  {
                    key: 'label',
                    className: 'remote-config-panel__label',
                  },
                  'Description',
                ),
                React.createElement(
                  'div',
                  {
                    key: 'value',
                    className: 'remote-config-panel__value',
                  },
                  configuration.description,
                ),
              ],
            ),

          // Default value
          React.createElement(
            'div',
            {
              key: 'default',
              className: 'remote-config-panel__section',
            },
            [
              React.createElement(
                'div',
                {
                  key: 'label',
                  className: 'remote-config-panel__label',
                },
                'Default Value',
              ),
              React.createElement(
                'div',
                {
                  key: 'value',
                  className: 'remote-config-panel__value',
                  style: {
                    color: isEnabled ? '#065f46' : 'var(--color-grey-45)',
                    fontWeight: 600,
                  },
                },
                isEnabled ? 'Enabled' : 'Disabled',
              ),
            ],
          ),

          // Environments
          environments.length > 0 &&
            React.createElement(
              'div',
              {
                key: 'environments',
                className: 'remote-config-panel__section',
              },
              [
                React.createElement(
                  'div',
                  {
                    key: 'label',
                    className: 'remote-config-panel__label',
                  },
                  'Environments',
                ),
                React.createElement(
                  'div',
                  {
                    key: 'list',
                    className: 'remote-config-panel__list',
                  },
                  environments.map(function (env, index) {
                    return React.createElement(
                      'span',
                      {
                        key: env + '-' + index,
                        className:
                          'remote-config-env-badge remote-config-env-badge--' +
                          env,
                      },
                      getEnvironmentLabel(env),
                    );
                  }),
                ),
              ],
            ),

          // Distributions
          renderListSection('Distributions', configuration.distributions),

          // Farming Units
          renderListSection('Farming Units', configuration.farming_units),

          // Users
          renderListSection('Users', configuration.users_email),

          // Extra data
          configuration.extra_data &&
            configuration.extra_data !== '{}' &&
            configuration.extra_data !== 'null' &&
            React.createElement(
              'div',
              {
                key: 'extra',
                className: 'remote-config-panel__section',
              },
              [
                React.createElement(
                  'div',
                  {
                    key: 'label',
                    className: 'remote-config-panel__label',
                  },
                  'Extra Data',
                ),
                React.createElement(
                  'div',
                  {
                    key: 'value',
                    className: 'remote-config-panel__value',
                    style: {
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      backgroundColor: 'var(--color-grey-96)',
                      padding: '8px',
                      borderRadius: '4px',
                      wordBreak: 'break-all',
                    },
                  },
                  configuration.extra_data,
                ),
              ],
            ),

          // Metadata
          React.createElement(
            'div',
            {
              key: 'meta',
              className: 'remote-config-panel__section',
            },
            [
              React.createElement(
                'div',
                {
                  key: 'label',
                  className: 'remote-config-panel__label',
                },
                'Metadata',
              ),
              React.createElement(
                'div',
                {
                  key: 'value',
                  className: 'remote-config-panel__meta',
                },
                [
                  configuration.created_at &&
                    React.createElement(
                      'span',
                      { key: 'created' },
                      'Created: ' +
                        formatRelativeTime(configuration.created_at) +
                        (configuration.created_by
                          ? ' by ' + configuration.created_by
                          : ''),
                    ),
                  configuration.updated_at &&
                    React.createElement(
                      'span',
                      { key: 'updated' },
                      'Updated: ' +
                        formatRelativeTime(configuration.updated_at) +
                        (configuration.updated_by
                          ? ' by ' + configuration.updated_by
                          : ''),
                    ),
                  !configuration.created_at &&
                    !configuration.updated_at &&
                    React.createElement(
                      'span',
                      {
                        key: 'none',
                        style: { fontStyle: 'italic' },
                      },
                      'No metadata available',
                    ),
                ],
              ),
            ],
          ),
        ],
      ),

      // Footer with actions
      React.createElement(
        'div',
        {
          key: 'footer',
          className: 'remote-config-panel__footer',
        },
        [
          React.createElement(
            'button',
            {
              key: 'edit',
              className: 'remote-config-btn-primary',
              onClick: handleEditClick,
              style: { flex: 1 },
            },
            'Edit',
          ),
          React.createElement(
            'button',
            {
              key: 'delete',
              className: 'remote-config-btn-danger',
              onClick: handleDeleteClick,
            },
            'Delete',
          ),
        ],
      ),
    ],
  );
};

module.exports = ConfigurationDetailsPanel;
